import { useState, useCallback } from "react";
import { Star } from "lucide-react";

interface StarRatingProps {
  value: number;
  onChange: (rating: number) => void;
  size?: "sm" | "md" | "lg";
  readonly?: boolean;
  label?: string;
}

export function StarRating({
  value,
  onChange,
  size = "lg",
  readonly = false,
  label = "Rating",
}: StarRatingProps) {
  const [hovered, setHovered] = useState(0);
  const [focused, setFocused] = useState(0);

  const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-10 w-10",
    lg: "h-14 w-14",
  };

  const containerClasses = {
    sm: "gap-1",
    md: "gap-2",
    lg: "gap-3",
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, star: number) => {
      if (readonly) return;

      switch (e.key) {
        case "ArrowRight":
        case "ArrowUp":
          e.preventDefault();
          if (star < 5) {
            onChange(star + 1);
          }
          break;
        case "ArrowLeft":
        case "ArrowDown":
          e.preventDefault();
          if (star > 1) {
            onChange(star - 1);
          }
          break;
        case "Home":
          e.preventDefault();
          onChange(1);
          break;
        case "End":
          e.preventDefault();
          onChange(5);
          break;
      }
    },
    [onChange, readonly]
  );

  const getStarLabel = (star: number) => {
    if (star === 1) return "1 star";
    return `${star} stars`;
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-readonly={readonly}
      className={`flex items-center ${containerClasses[size]}`}
      onMouseLeave={() => !readonly && setHovered(0)}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= (hovered || focused || value);
        const isSelected = star === value;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={getStarLabel(star)}
            disabled={readonly}
            tabIndex={readonly ? -1 : isSelected || (value === 0 && star === 1) ? 0 : -1}
            onClick={() => !readonly && onChange(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onFocus={() => !readonly && setFocused(star)}
            onBlur={() => setFocused(0)}
            onKeyDown={(e) => handleKeyDown(e, star)}
            className={`transition-all duration-200 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              readonly ? "cursor-default" : "cursor-pointer hover:scale-110"
            }`}
            data-testid={`star-${star}`}
          >
            <Star
              className={`${sizeClasses[size]} transition-colors ${
                isFilled
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-transparent text-muted-foreground/40"
              }`}
              aria-hidden="true"
            />
          </button>
        );
      })}
      <span className="sr-only">
        {value > 0 ? `Currently rated ${value} out of 5 stars` : "No rating selected"}
      </span>
    </div>
  );
}
